





// Chat data models for the new simplified chat system

export type ChatType = 'one_on_one' | 'group' | 'client_discussion' | 'admin_client';

export interface ChatUser {
  id: string;
  name: string;
  role: 'admin' | 'staff' | 'client' | 'limited_access';
  photoURL?: string; // Firebase auth photoURL
}

export interface Conversation {
  id: string;
  type: ChatType;
  name?: string; // For group chats and client discussions
  participants: string[]; // User IDs
  participantDetails?: { [userId: string]: { name: string; photoURL?: string; role?: string } }; // Participant info for avatars
  clientId?: string; // For client_discussion type
  clientName?: string; // For display purposes
  otherUserName?: string; // For one-on-one chats
  otherUserAvatar?: string; // For one-on-one chats
  createdAt: Date;
  createdBy: string;
  lastMessage?: string;
  lastMessageAt?: Date;
  lastMessageBy?: string;
  // Track read status per user
  lastReadBy?: { [userId: string]: Date }; // userId -> last read timestamp
  lastReadMessageId?: { [userId: string]: string }; // Last message ID each user has read
  unreadCount?: { [userId: string]: number }; // userId -> unread message count
}

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderPhotoURL?: string; // Add avatar support
  content: string;
  createdAt: Date;
  type?: 'text' | 'image' | 'file' | 'call' | 'emergency_location'; // Add message types
  attachmentUrl?: string; // For images and files
  attachmentName?: string; // Original filename for files
  attachmentSize?: number; // File size in bytes
  fileType?: string; // Native media kind: 'image' | 'video' | 'document'
  fileUrl?: string; // Native file/video download URL
  imageUrl?: string; // Native image download URL
  thumbnailUrl?: string; // Native-generated video poster frame
  duration?: number; // For call messages - duration in seconds
  callStatus?: 'completed' | 'missed' | 'declined'; // For call messages
  isEmergency?: boolean; // Emergency call line (native after-hours flow)
  emergencyReason?: string; // 'Sick' | 'Ambulance' | 'Police' | 'Fire' | 'Bad Problem' | 'Other'
  emergencyLocation?: {
    status?: 'ok' | 'denied' | 'off' | 'unavailable';
    latitude?: number;
    longitude?: number;
    accuracy?: number;
    address?: string;
  };
  readBy?: Array<{ userId: string; timestamp: Date }>; // Array of users who read with timestamps
  reactions?: { [userId: string]: string }; // User reactions (userId: emoji)
  // Messenger-style reply — denormalised snapshot of the original message.
  // Field names MUST match the native app exactly: {id, text, senderName, type}.
  replyTo?: {
    id: string;
    text: string;
    senderName: string;
    type?: string;
  } | null;
}

export interface ConversationCreateData {
  type: ChatType;
  name?: string; // For group chats
  participants: string[]; // User IDs
  clientId?: string;
  clientName?: string; // Optional client name to avoid API call
  directMembers?: string[]; // Native "Office Admin" direct chats: [the chosen staff/contractor uid]
  participantSeeds?: Array<{ id: string; name: string; photoURL?: string | null; role?: string }>; // Pre-resolved details (e.g. hidden super-admins the stores can't see)
}

// UI state interfaces
export interface ChatState {
  conversations: Conversation[];
  selectedConversation: Conversation | null;
  messages: Record<string, Message[]>; // conversationId -> messages
  isLoading: boolean;
  error: string | null;
}

// ── Shared unread badge helper (mirrors React Native build 2.6.0) ──
// Single source of truth for whether `conversation` should render an
// unread badge for `currentUserId`. Used by ConversationList,
// ChatSidebar, and AppProvider so the three surfaces never disagree.
//
//   1. No `lastMessageAt` → never unread (empty chat).
//   2. Bug 1 guard — I sent the most recent message → never unread.
//   3. Shared `unreadCount.{uid}` map is preferred when present —
//      carries the admin_client team-acknowledge state set by
//      `buildUnreadClearsForRead` in chatService.
//   4. Legacy fallback — per-user `lastReadBy.{uid}` timestamp vs.
//      `lastMessageAt`.
export const isConversationUnreadFor = (
  conversation: Conversation,
  currentUserId: string,
): boolean => {
  if (!conversation.lastMessageAt || !conversation.lastMessageBy) return false;
  if (conversation.lastMessageBy === currentUserId) return false;
  const counter = conversation.unreadCount?.[currentUserId];
  if (typeof counter === 'number') return counter > 0;
  const lastReadTime = conversation.lastReadBy?.[currentUserId];
  if (!lastReadTime) return true;
  return conversation.lastMessageAt > lastReadTime;
};
